import React from "react";
import {
  AlertIcon,
  CornerDownRightIcon,
  GlobeIcon,
  HeartIcon,
  LightningIcon,
  MinusCircleIcon,
  MobilePhoneIcon,
  MoneyIcon,
  PersonIcon,
  PersonWithCheckIcon,
  RefreshIcon,
  SearchIcon,
  SettingsIcon,
  SparklesIcon,
  SunshineIcon,
  TargetIcon,
  TrendIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/healthcare.png";
import { dataBreachStat, revenueReferenceCopy, type CdpIndustryConfig } from "./shared";

export const healthcareCdpConfig: CdpIndustryConfig = {
  icon: HeartIcon,
  name: "healthcare",
  pluralNoun: "healthcare companies",
  color: "yellow",
  solutionPageSlug: "/solutions/healthcare",
  seoCopy:
    "Leverage all your healthcare data, including patients, providers, appointments, medical records, prescriptions, and insurance claims.",
  heroImage: {
    src: hero,
    alt: "A photograph of two people looking at each other with a list of example data tables overlayed. The data tables included are for Medical Records, Insurance Claims, Patients, Appointments, and Providers.",
  },
  adoptingCdpsDescription: (
    <>
      Healthcare is shifting toward personalization, and patients expect seamless, tech-enabled care.{" "}
      {revenueReferenceCopy}
    </>
  ),
  featuredOutcomes: [
    {
      icon: HeartIcon,
      color: "red",
      title: "Build loyalty",
    },
    {
      icon: RefreshIcon,
      color: "yellow",
      title: "Reduce churn",
    },
    {
      icon: PersonIcon,
      color: "green",
      title: "Drive engagement",
    },
  ],
  dataStats: [
    {
      title: "$6.8T",
      description: (
        <>
          Estimated healthcare
          <br />
          spend by 2028
        </>
      ),
      href: "https://www.cms.gov/newsroom/press-releases/cms-office-actuary-releases-2021-2030-projections-national-health-expenditures",
    },
    {
      title: "60%",
      description: (
        <>
          Of patients expect
          <br /> personalized experiences
        </>
      ),
      href: "https://www.redpointglobal.com/blog/healthcare-consumers-weigh-in-personalized-experiences-are-a-must",
    },
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title: "UnitedHealth uses AI to offer tailored solutions and decrease the cost of care for members",
      href: "https://emerj.com/artificial-intelligence-at-united-health/",
    },
    {
      title:
        "CVS Health delivers personalized experiences to more than 55 million digital customers through mobile app",
      href: "https://www.constellationr.com/blog-news/insights/cvs-health-s-transformation-rides-data-ai-and-customer-experiences",
    },
    {
      title: "Walgreens is powering a personalized loyalty program for more than 100 million members",
      href: "https://www.walgreensbootsalliance.com/news-media/press-releases/2020/walgreens-reinvents-nations-largest-health-and-wellbeing-centered-0",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new patient acquisition using demographic and health behavior data to target potential patients and lookalike audiences with offers like free health screenings or first-time consultations, increasing patient sign-ups.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by suppressing existing patients from acquisition campaigns using EHR or CRM data, reallocating budget toward new or lapsed patients to improve efficiency.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Re-engage patients who started but didn’t book an appointment by serving personalized reminders or incentives, recovering lost bookings and driving incremental visits.",
      },
      // Second row
      {
        icon: MoneyIcon,
        title: "Cross-sell campaigns",
        description:
          "Promote complementary healthcare services by using patient data to target individuals with relevant ads for related services, increasing patient lifetime value and care engagement.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Healthcare media networks",
        description:
          "Monetize anonymized patient insights for pharma and wellness brand advertising by creating audience segments (e.g., patients seeking wellness products), driving incremental revenue through premium audience insights.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: PersonIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Recommend related services like dietitian consultations for diabetic patients or dental cleanings for orthodontic patients, increasing patient retention.",
      },
      {
        icon: RefreshIcon,
        title: "Reactivation campaigns",
        description:
          "Re-engage lapsed patients with reminders and incentives for annual checkups or preventative care, improving engagement.",
      },
      {
        icon: AlertIcon,
        title: "Preventative care reminders",
        description:
          "Send personalized reminders for health screenings, checkups, and flu shots, increasing preventative care visits.",
      },
      // Second row
      {
        icon: SunshineIcon,
        title: "Seasonal health campaigns",
        description:
          "Promote seasonal healthcare needs like flu shots in fall or allergy treatments in spring, increasing appointment volume.",
      },
      {
        icon: HeartIcon,
        title: "Chronic care engagement",
        description:
          "Provide targeted messages encouraging medication refills, check-ins, or lifestyle consultations, improving adherence.",
      },
      {
        icon: SparklesIcon,
        title: "Wellness challenge campaigns",
        description: "Engage patients with gamified wellness programs rewarding them for achieving health milestones.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LightningIcon,
        title: "Dynamic home page content",
        description:
          "Display personalized health tips, reminders, and service suggestions based on patient history or interests to encourage engagement and proactive healthcare management.",
      },
      {
        icon: MobilePhoneIcon,
        title: "Dynamic app experiences",
        description:
          "Customize app navigation by prioritizing commonly used features such as appointment booking, prescription tracking, and telehealth access to improve patient satisfaction and retention.",
      },
      {
        icon: SearchIcon,
        title: "Dynamic search results",
        description:
          "Tailor search filters and rankings to prioritize relevant healthcare services, such as showing top specialists for specific conditions, making service discovery seamless.",
      },
      // Second row
      {
        icon: PersonWithCheckIcon,
        title: "Recommended services",
        description:
          "Suggest follow-up healthcare services, such as physical therapy, nutrition counseling, or wellness programs, based on recent visits to drive continued engagement and improve care continuity.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Guide patients toward high-impact health actions like scheduling screenings, joining wellness programs, or completing health assessments to enhance patient outcomes and service utilization.",
      },
      {
        icon: TrendIcon,
        title: "Health goal tracking",
        description:
          "Encourage wellness engagement by displaying progress on health-related goals such as step counts, weight loss, or chronic condition management, reinforcing motivation with rewards or encouragement.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional: "Uses predefined models that may not fully capture patient records or treatment pathways",
      composable:
        "Supports tailored models to handle healthcare-specific data like patient histories, treatment plans, and diagnostic codes",
    },
    audienceManagement: {
      traditional:
        "Supports broad segmentation but struggles with identifying groups like chronic care patients or wellness program participants",
      composable: "Enables granular segmentation by patient condition, care plans, or insurance coverage",
    },
    customerJourneys: {
      traditional: "Provides standard templates for appointment reminders or general follow-up campaigns",
      composable:
        "Powers fully adaptable journeys for patient pathways like pre-surgery preparation, chronic care management, and post-care outreach",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across EMR systems and clinic networks",
      composable:
        "Supports custom algorithms to unify patient profiles across EHRs, telehealth platforms, and in-clinic records",
    },
  },
  whyHightouchTitleMaxW: "750px",
  audienceOverlap: {
    title: "Engaged patients with unscheduled follow-ups",
    firstCategoryLabel: "Missed appointment patients",
    secondCategoryLabel: "Patient portal registered users",
  },
};
